import {GqlModel, GqlModelOperator, GqlMutationModel, GqlQueryModel} from "../../graphql/graphql.model";
import gql from "graphql-tag";
import {WorkflowStepStatus} from "./workflow-step-status.enum";
import {JSONModel, jsonProperty} from "../../json.model";
import {SuccessResultGqlModel} from "../../graphql/common.gql-model";

export class WorkflowStepUpdateGqlModel extends JSONModel {
  @jsonProperty("number") timestamp: number;
  @jsonProperty("string") userId: string;
  @jsonProperty("string") fullName: string;
  @jsonProperty("string") emailAddress: string;
  @jsonProperty("date", "timestamp") date: Date;
}

export class WorkflowStepGqlModel extends JSONModel {
  @jsonProperty("string") stepName: string;
  @jsonProperty("number") status: WorkflowStepStatus;
  @jsonProperty(["string"]) roles: string[];
  @jsonProperty("string") readableStepName: string;
  @jsonProperty("string") prompt: string;
  @jsonProperty("string") workflowId: string;
  @jsonProperty("string") url: string;
  @jsonProperty("boolean") blocked: boolean; // typically pre-fills false, but WorkflowService will adjust
  @jsonProperty("boolean") previousStepNeedsWork: boolean;
  @jsonProperty(WorkflowStepUpdateGqlModel) lastUpdate: WorkflowStepUpdateGqlModel;
  @jsonProperty("boolean") hasNewRoles: boolean;
}

export class WorkflowBreadcrumbItemGqlModel extends JSONModel {
  @jsonProperty("string") text: string;
  @jsonProperty("string") queryAction: string;
  @jsonProperty("json{}") queryParams: {[key: string]: string};
}

export class WorkflowStepStatusGqlModel extends GqlMutationModel {
  static mapper: GqlModelOperator<SuccessResultGqlModel> = GqlModel.buildMapper(
    SuccessResultGqlModel,
    "workflowStepStatus",
  );

  static query: any = gql`
    mutation workflowStepStatus($workflowId: ID!, $stepName: String!, $status: Int!) {
      workflowStepStatus(workflowId: $workflowId, stepName: $stepName, status: $status) {
        success
      }
    }
  `;

  @jsonProperty("string") workflowId: string;
  @jsonProperty("string") stepName: string;
  @jsonProperty("number") status: WorkflowStepStatus;
}

export class WorkflowFlagGqlModel extends GqlMutationModel {
  static mapper: GqlModelOperator<SuccessResultGqlModel> = GqlModel.buildMapper(SuccessResultGqlModel, "workflowFlag");

  static query: any = gql`
    mutation workflowFlag($workflowId: ID!, $flagName: String!, $flagValue: JSON) {
      workflowFlag(workflowId: $workflowId, flagName: $flagName, flagValue: $flagValue) {
        success
      }
    }
  `;

  @jsonProperty("string") workflowId: string;
  @jsonProperty("string") flagName: string;
  @jsonProperty("JSON.stringify") flagValue: any;
}

export class WorkflowFinishGqlModel extends GqlMutationModel {
  static mapper: GqlModelOperator<SuccessResultGqlModel> = GqlModel.buildMapper(
    SuccessResultGqlModel,
    "workflowFinish",
  );

  static query: any = gql`
    mutation workflowFinish($workflowId: ID!) {
      workflowFinish(workflowId: $workflowId) {
        success
      }
    }
  `;

  @jsonProperty("string") workflowId: string;
}

export class WorkflowGqlModel extends GqlQueryModel {
  static mapper: GqlModelOperator<WorkflowGqlModel> = GqlModel.buildMapper(WorkflowGqlModel, "workflow");

  static query: any = gql`
    fragment workflowStepData on WorkflowStep {
      stepName
      status
      roles
      readableStepName
      prompt
      workflowId
      url
      blocked
      previousStepNeedsWork
      hasNewRoles

      lastUpdate {
        timestamp
        userId
        fullName
        emailAddress
      }
    }
    
    subscription workflow($workflowId: ID!) {
      workflow(workflowId: $workflowId) {
        id
        type
        targetId
        projectId
        version
        finished

        readableTargetName
        readableTargetType
        
        filledPrivileges
        
        geoRegionId
        countryId
        targetSystem

        flags

        target

        steps {
          ...workflowStepData
        }

        breadcrumb {
          text
          queryAction
          queryParams
        }
      }
    }
  `;

  @jsonProperty("string") id: string;
  @jsonProperty("string") type: string;
  @jsonProperty("string") targetId: string;
  @jsonProperty("string") projectId: string;
  @jsonProperty("number") version: number;
  @jsonProperty("date") finished: Date;

  @jsonProperty("string") readableTargetName: string;
  @jsonProperty("string") readableTargetType: string;

  @jsonProperty(["string"]) filledPrivileges: string[];

  @jsonProperty("string") geoRegionId: string;
  @jsonProperty("string") countryId: string;
  @jsonProperty("string") targetSystem: string;

  @jsonProperty("json{}") flags: {[key: string]: any};

  @jsonProperty([WorkflowStepGqlModel]) steps: WorkflowStepGqlModel[];

  @jsonProperty([WorkflowBreadcrumbItemGqlModel]) breadcrumb: WorkflowBreadcrumbItemGqlModel[];

  @jsonProperty("json{}") target: {[key: string]: any};

  static variables(workflowId: string) {
    return {workflowId};
  }
}
